import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

import { AppearanceService } from '../../services/appearance.service';
import { MaterialService } from '../../services/material.service';
import { PointcloudService } from '../../services/pointcloud.service';
import { TerrainService } from '../../services/terrain.service';
import type { VectorTileLayerConfig } from '../../types/vector-tile-layer';
import { DropdownComponent, SliderComponent } from '../../ui';
import {
  ClassificationItem,
  ClassificationPanelComponent,
} from '../../ui/classification-panel/classification-panel.component';

@Component({
  selector: 'app-layers-panel',
  imports: [
    CommonModule,
    DropdownComponent,
    SliderComponent,
    ClassificationPanelComponent,
    TranslocoPipe,
  ],
  templateUrl: './layers-panel.component.html',
  styleUrls: ['./layers-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayersPanelComponent {
  private appearance = inject(AppearanceService);
  private material = inject(MaterialService);
  private pointcloud = inject(PointcloudService);
  private terrain = inject(TerrainService);
  private translocoService = inject(TranslocoService);

  // Layer visibility state
  pointCloudVisible = signal(true);
  tilesVisible = signal(false);
  terrainMapVisible = signal(false);
  terrainAerialVisible = signal(false);

  // Internal vector tile layers, offered once terrain is active
  vectorTileLayerCatalog = computed(() => this.terrain.vectorTileLayerCatalog());
  activeVectorTileLayers = signal<string[]>([]);

  // Loading state
  tilesLoading = signal(false);
  terrainMapLoading = signal(false);
  terrainAerialLoading = signal(false);
  terrainLoading = signal(false);

  // Loaded state
  tilesLoaded = signal(false);
  terrainLoaded = signal(false);

  // Digital twin models overlay
  digitalTwinVisible = signal(false);
  digitalTwinLoading = signal(false);

  /** Whether any terrain background (map or aerial) is currently active. */
  get isTerrainActive(): boolean {
    return this.terrainMapVisible() || this.terrainAerialVisible();
  }

  get currentBackground(): string {
    return this.appearance.background();
  }
  get backgroundOptions() {
    return this.appearance.getBackgroundOptions();
  }

  get currentColorAttribute(): string {
    return this.material.colorAttribute();
  }
  get colorOptions() {
    return this.material.getColorAttributes();
  }
  get rgbGamma(): number {
    return this.material.rgbGamma();
  }
  get rgbBrightness(): number {
    return this.material.rgbBrightness();
  }
  get rgbContrast(): number {
    return this.material.rgbContrast();
  }
  get intensityGamma(): number {
    return this.material.intensityGamma();
  }
  get intensityBrightness(): number {
    return this.material.intensityBrightness();
  }
  get intensityContrast(): number {
    return this.material.intensityContrast();
  }
  get solidColor(): string {
    return this.material.solidColor();
  }
  get classificationItems(): ClassificationItem[] {
    return this.pointcloud.classifications();
  }

  get isRGBActive(): boolean {
    return this.currentColorAttribute === 'rgba' || this.currentColorAttribute === 'rgb';
  }
  get isIntensityActive(): boolean {
    return (
      this.currentColorAttribute === 'intensity' ||
      this.currentColorAttribute === 'intensity gradient'
    );
  }
  get isColorPickerActive(): boolean {
    return this.currentColorAttribute === 'color';
  }
  get isClassificationActive(): boolean {
    return this.currentColorAttribute === 'classification';
  }

  constructor() {
    this.refreshLayerState();
    void this.terrain.loadVectorTileLayerCatalog();
  }

  private refreshLayerState(): void {
    this.pointCloudVisible.set(this.pointcloud.pointCloudVisible());
    this.tilesLoaded.set(this.terrain.terrainLoaded());
    this.tilesVisible.set(this.terrain.terrainEnabled());
    this.terrainLoaded.set(this.terrain.quantizedTerrainLoaded());
    const terrainEnabled = this.terrain.quantizedTerrainEnabled();
    const layer = this.terrain.textureLayer();
    this.terrainMapVisible.set(terrainEnabled && layer === 'map');
    this.terrainAerialVisible.set(terrainEnabled && layer === 'aerial');
    this.digitalTwinVisible.set(this.terrain.digitalTwinVisible());
    this.activeVectorTileLayers.set(this.terrain.featureLayers());
  }

  async togglePointCloud(): Promise<void> {
    const visible = !this.pointCloudVisible();
    this.pointCloudVisible.set(visible);
    this.pointcloud.pointCloudVisible.set(visible);

    // Digital twin models follow the same toggle, but only while terrain is active.
    await this.syncDigitalTwinModels(visible);
  }

  /**
   * Keep the digital twin overlay in sync with the point cloud toggle.
   * `setDigitalTwinModelsVisible(true)` / loading only happens when a terrain
   * background is active.
   */
  private async syncDigitalTwinModels(visible: boolean): Promise<void> {
    if (visible && this.isTerrainActive) {
      this.digitalTwinLoading.set(true);
      try {
        await this.terrain.loadDigitalTwinModels();
        this.digitalTwinVisible.set(true);
      } catch (error) {
        console.error('Error loading digital twin models:', error);
        this.digitalTwinVisible.set(false);
      } finally {
        this.digitalTwinLoading.set(false);
      }
    } else {
      this.digitalTwinVisible.set(false);
      this.terrain.setDigitalTwinModelsVisible(false);
    }
  }

  async toggleTiles(): Promise<void> {
    if (!this.tilesLoaded()) {
      this.tilesLoading.set(true);
      try {
        await this.terrain.loadSwissTerrain();
        this.tilesLoaded.set(true);
        this.tilesVisible.set(true);
      } catch (error) {
        console.error('Error loading 3D tiles:', error);
      } finally {
        this.tilesLoading.set(false);
      }
    } else {
      this.tilesVisible.update(v => !v);
      this.terrain.setTerrainEnabled(this.tilesVisible());
    }
  }

  async toggleTerrainMap(): Promise<void> {
    // If already active, toggle off
    if (this.terrainMapVisible()) {
      this.terrainMapVisible.set(false);
      this.terrain.setQuantizedTerrainEnabled(false);
      return;
    }

    // Disable aerial if active
    this.terrainAerialVisible.set(false);

    if (!this.terrainLoaded()) {
      this.terrainMapLoading.set(true);
      try {
        await this.terrain.loadQuantizedMeshTerrain('map');
        this.terrainLoaded.set(true);
        this.terrainMapVisible.set(true);
      } catch (error) {
        console.error('Error loading terrain (map):', error);
      } finally {
        this.terrainMapLoading.set(false);
      }
    } else {
      this.terrainMapLoading.set(true);
      try {
        await this.terrain.setQuantizedTerrainTextureLayer('map');
        this.terrain.setQuantizedTerrainEnabled(true);
        this.terrainMapVisible.set(true);
      } finally {
        this.terrainMapLoading.set(false);
      }
    }
  }

  async toggleTerrainAerial(): Promise<void> {
    // If already active, toggle off
    if (this.terrainAerialVisible()) {
      this.terrainAerialVisible.set(false);
      this.terrain.setQuantizedTerrainEnabled(false);
      return;
    }

    // Disable map if active
    this.terrainMapVisible.set(false);

    if (!this.terrainLoaded()) {
      this.terrainAerialLoading.set(true);
      try {
        await this.terrain.loadQuantizedMeshTerrain('aerial');
        this.terrainLoaded.set(true);
        this.terrainAerialVisible.set(true);
      } catch (error) {
        console.error('Error loading terrain (aerial):', error);
      } finally {
        this.terrainAerialLoading.set(false);
      }
    } else {
      this.terrainAerialLoading.set(true);
      try {
        await this.terrain.setQuantizedTerrainTextureLayer('aerial');
        this.terrain.setQuantizedTerrainEnabled(true);
        this.terrainAerialVisible.set(true);
      } finally {
        this.terrainAerialLoading.set(false);
      }
    }
  }

  async selectTerrainBackground(type: 'none' | 'map' | 'aerial'): Promise<void> {
    if (type === 'none') {
      this.terrainMapVisible.set(false);
      this.terrainAerialVisible.set(false);
      this.terrain.setQuantizedTerrainEnabled(false);
      // Terrain is off → hide the digital twin overlay too.
      this.digitalTwinVisible.set(false);
      this.terrain.setDigitalTwinModelsVisible(false);
      // ...and any internal vector tile layers draped on it.
      this.terrain.disableAllVectorTileLayers();
      this.activeVectorTileLayers.set(this.terrain.featureLayers());
      return;
    }

    // If already showing the selected type, do nothing
    if (type === 'map' && this.terrainMapVisible()) return;
    if (type === 'aerial' && this.terrainAerialVisible()) return;

    // Immediately show the clicked button as selected
    this.terrainMapVisible.set(type === 'map');
    this.terrainAerialVisible.set(type === 'aerial');

    const loadingSignal = type === 'map' ? this.terrainMapLoading : this.terrainAerialLoading;

    if (!this.terrainLoaded()) {
      loadingSignal.set(true);
      try {
        await this.terrain.loadQuantizedMeshTerrain(type);
        this.terrainLoaded.set(true);
      } catch (error) {
        console.error(`Error loading terrain (${type}):`, error);
        this.terrainMapVisible.set(false);
        this.terrainAerialVisible.set(false);
      } finally {
        loadingSignal.set(false);
      }
    } else {
      loadingSignal.set(true);
      try {
        await this.terrain.setQuantizedTerrainTextureLayer(type);
        this.terrain.setQuantizedTerrainEnabled(true);
      } finally {
        loadingSignal.set(false);
      }
    }

    // Terrain is now active → align the digital twin overlay with the toggle.
    await this.syncDigitalTwinModels(this.pointCloudVisible());
  }

  isVectorTileLayerActive(layerId: string): boolean {
    return this.activeVectorTileLayers().includes(layerId);
  }

  /** Localized label for a vector tile layer, per the currently active language. */
  vectorTileLayerLabel(layer: VectorTileLayerConfig): string {
    return this.translocoService.getActiveLang() === 'de' ? layer.labelDe : layer.labelEn;
  }

  /** Vector tile layers can only be draped while a terrain background is active. */
  toggleVectorTileLayer(layerId: string): void {
    if (!this.isTerrainActive) return;
    this.terrain.toggleVectorTileLayer(layerId);
    this.activeVectorTileLayers.set(this.terrain.featureLayers());
  }

  onBackgroundChange(b: string): void {
    this.appearance.background.set(b);
  }

  onColorAttributeChange(a: string): void {
    this.material.colorAttribute.set(a);
  }

  onRGBGammaChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.material.rgbGamma.set(v);
    }
  }

  onRGBBrightnessChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.material.rgbBrightness.set(v);
    }
  }

  onRGBContrastChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.material.rgbContrast.set(v);
    }
  }

  onIntensityGammaChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.material.intensityGamma.set(v);
    }
  }

  onIntensityBrightnessChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.material.intensityBrightness.set(v);
    }
  }

  onIntensityContrastChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.material.intensityContrast.set(v);
    }
  }

  onSolidColorChange(color: string): void {
    this.material.solidColor.set(color);
  }
}
